import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Globe, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const sections = [
    {
      icon: Database,
      title: isRTL ? 'المعلومات التي نجمعها' : 'Information We Collect',
      content: isRTL 
        ? 'نقوم بجمع المعلومات التي تقدمها مباشرة لنا، بما في ذلك الاسم والبريد الإلكتروني ومعلومات الشركة عند التسجيل. كما نجمع بيانات الاستخدام تلقائياً لتحسين خدماتنا.'
        : 'We collect information you provide directly to us, including name, email, and company information when you register. We also automatically collect usage data to improve our services.'
    },
    {
      icon: Eye,
      title: isRTL ? 'كيف نستخدم معلوماتك' : 'How We Use Your Information',
      content: isRTL
        ? 'نستخدم المعلومات لتقديم وصيانة خدماتنا، ومعالجة المعاملات، وإرسال الإشعارات، وتحسين تجربة المستخدم، والامتثال للمتطلبات القانونية.'
        : 'We use information to provide and maintain our services, process transactions, send notifications, improve user experience, and comply with legal requirements.'
    },
    {
      icon: UserCheck,
      title: isRTL ? 'مشاركة البيانات' : 'Data Sharing',
      content: isRTL
        ? 'لا نبيع معلوماتك الشخصية. قد نشارك البيانات مع مقدمي الخدمات الموثوقين الذين يساعدوننا في تشغيل أعمالنا، وفقط بموجب اتفاقيات سرية صارمة.'
        : 'We do not sell your personal information. We may share data with trusted service providers who help us operate our business, only under strict confidentiality agreements.'
    },
    {
      icon: Lock,
      title: isRTL ? 'أمان البيانات' : 'Data Security',
      content: isRTL
        ? 'نستخدم تشفير SSL/TLS، وجدران حماية متقدمة، ومراقبة أمنية على مدار الساعة لحماية بياناتك. جميع البيانات مشفرة أثناء النقل والتخزين.'
        : 'We use SSL/TLS encryption, advanced firewalls, and 24/7 security monitoring to protect your data. All data is encrypted in transit and at rest.'
    },
    {
      icon: Globe,
      title: isRTL ? 'نقل البيانات الدولي' : 'International Data Transfer',
      content: isRTL
        ? 'قد يتم نقل بياناتك ومعالجتها في دول مختلفة. نضمن الامتثال لقوانين حماية البيانات المعمول بها بما في ذلك GDPR.'
        : 'Your data may be transferred and processed in different countries. We ensure compliance with applicable data protection laws including GDPR.'
    },
    {
      icon: Shield,
      title: isRTL ? 'حقوقك' : 'Your Rights',
      content: isRTL
        ? 'لديك الحق في الوصول إلى بياناتك وتصحيحها وحذفها ونقلها. يمكنك أيضاً الاعتراض على المعالجة وسحب الموافقة في أي وقت.'
        : 'You have the right to access, correct, delete, and port your data. You can also object to processing and withdraw consent at any time.'
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* SEO Title */}
      <Helmet>
        <title>Privacy Policy | FlowFeed POS</title>
      </Helmet>
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className="h-4 w-4" />
                {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
            <h1 className="text-xl font-bold">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</h1>
            <div className="w-32" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</h1>
          <p className="text-muted-foreground">
            {isRTL ? 'آخر تحديث: ديسمبر 2024' : 'Last updated: December 2024'}
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <p className="text-muted-foreground leading-relaxed">
              {isRTL 
                ? 'في فلوفيد ERP، نأخذ خصوصيتك على محمل الجد. توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية معلوماتك الشخصية عند استخدام خدماتنا.'
                : 'At FlowFeed ERP, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information when you use our services.'
              }
            </p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {sections.map((section, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="p-2 rounded-lg bg-primary/10">
                    <section.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h3 className="font-semibold text-lg mb-2">{section.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="mt-8 bg-muted/50">
          <CardContent className="p-6 text-center">
            <Mail className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">{isRTL ? 'أسئلة حول الخصوصية؟' : 'Privacy Questions?'}</h3>
            <p className="text-muted-foreground mb-4">
              {isRTL ? 'تواصل معنا على' : 'Contact us at'}
            </p>
            <a href="mailto:feed@maxefeed.com" className="text-primary hover:underline font-medium">
              feed@maxefeed.com
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
